import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import type { ContactProfilesEnabledResp } from "@/types/contactProfile.ts";

/**
 * Whether contact profiles are enabled for the signed-in account — mirrors the
 * server-side `contact-profiles` routine enablement (the `routines` gate plus the
 * `routine-contact-profiles` rollout). Gates the People nav item, the `/people`
 * route, and the contact Bio tab.
 *
 * Returns `{ enabled, isLoading }` so callers can distinguish "loading" from
 * "disabled" — a route guard must not redirect while the query is in flight.
 * Enablement doesn't change mid-session, so it's fetched once and never refetched.
 */
export const useContactProfilesEnabled = (): { enabled: boolean; isLoading: boolean } => {
  const { data, isLoading } = useQuery({
    queryKey: ["contactProfilesEnabled"],
    queryFn: async ({ signal }) => {
      const response = await api.get<ContactProfilesEnabledResp>(
        "/api/a/contacts/profiles-enabled",
        {
          signal,
        },
      );
      return response.data.enabled;
    },
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  return { enabled: data ?? false, isLoading };
};
